/**
 * OpeningBalanceModeRadio — Phase 6c.2 per Frontend Spec §21.6.2.
 *
 * Vertical 3-option radio group for the opening balance methodology
 * (`IFRS_FIRST_ADOPTION` | `AUTO_COMPUTED` | `MANUAL`). Each option
 * renders a Big4-tone TR title and an audit-grade subtitle taken
 * verbatim from the i18n bundle (§21.15.1 — spec-authoritative copy).
 *
 *   • No default selection — the form's Zod superRefine rejects submit
 *     until the auditor explicitly picks a mode. Prevents accidental
 *     "AUTO_COMPUTED defaults that nobody checked".
 *   • Locked after engagement creation — the consumer page renders the
 *     component with `disabled` set; an amber locked-banner appears
 *     below the options. Phase 6a.19 (future) will introduce a
 *     controlled unlock flow.
 *
 * The component is presentation-only — it does NOT know about
 * react-hook-form or the persistence layer. The consumer wires it
 * through `FormField`/`Controller` (or any other state holder).
 */

import { useTranslation } from "react-i18next";

import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  OPENING_BALANCE_MODES,
  type OpeningBalanceMode,
} from "@/types/engagement";

export interface OpeningBalanceModeRadioProps {
  /** Current selection. `undefined` = no selection (initial state). */
  value: OpeningBalanceMode | undefined;
  onChange: (mode: OpeningBalanceMode) => void;
  /** Locked after engagement creation (Spec §21.6.3). */
  disabled?: boolean;
}

export function OpeningBalanceModeRadio({
  value,
  onChange,
  disabled,
}: OpeningBalanceModeRadioProps) {
  const { t } = useTranslation();
  return (
    <div
      className="rounded-lg border bg-card p-4 space-y-1"
      data-testid="opening-balance-mode-radio"
    >
      <Label className="text-sm font-medium">
        {t("form.openingBalanceMode.label")}{" "}
        <span className="text-destructive">{t("form.required")}</span>
      </Label>
      <RadioGroup
        value={value ?? ""}
        onValueChange={(v) => onChange(v as OpeningBalanceMode)}
        disabled={disabled}
        className="mt-3 space-y-3"
      >
        {OPENING_BALANCE_MODES.map((mode) => (
          <div key={mode} className="flex items-start space-x-3">
            <RadioGroupItem
              value={mode}
              id={`mode-${mode}`}
              className="mt-0.5"
            />
            <div className="flex-1">
              <Label
                htmlFor={`mode-${mode}`}
                className="cursor-pointer text-sm font-medium"
              >
                {t(`form.openingBalanceMode.${mode}.title` as never) as string}
              </Label>
              <p className="mt-0.5 text-sm text-muted-foreground">
                {
                  t(
                    `form.openingBalanceMode.${mode}.description` as never,
                  ) as string
                }
              </p>
            </div>
          </div>
        ))}
      </RadioGroup>
      {disabled && (
        <p
          className="mt-2 text-xs italic text-amber-700 dark:text-amber-400"
          data-testid="opening-balance-mode-locked"
        >
          {t("form.openingBalanceMode.locked")}
        </p>
      )}
    </div>
  );
}
